/**
 * 213 (#309): the shape `backend/src/modules/bookings/conflictAlternatives.ts`'s `AlternativeCar`
 * serializes as, read off `ApiError.alternatives` (an untyped `unknown[]` — the backend's own
 * `details` payload has no fixed shape across every future use of it). Shared between
 * `BookingConfirmPage` (the payment-race refusal) and `BookingFormPage` (the create-time refusal) —
 * two call sites for the identical rendering, not worth two copies.
 */
export interface AlternativeCar {
  id: string;
  name: string;
  pricing: {
    per_hour_minor: number | null;
    per_day_minor: number | null;
    currency_symbol: string;
  } | null;
}

function formatAlternativePrice(pricing: AlternativeCar['pricing']): string | null {
  if (!pricing) return null;
  const minor = pricing.per_hour_minor ?? pricing.per_day_minor;
  if (minor == null) return null;
  const unit = pricing.per_hour_minor != null ? '/hr' : '/day';
  return `${pricing.currency_symbol}${(minor / 100).toFixed(2)}${unit}`;
}

/** A booking refused by a conflict names other cars free for the same window — a shortlist, not a
 * second search page (the backend's own cap is already applied). */
export function AlternativeCars({ items }: { items: AlternativeCar[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3" data-testid="alternative-cars">
      <p className="text-sm text-steel mb-2">Other cars free for this time:</p>
      <ul className="space-y-2">
        {items.map((car) => (
          <li
            key={car.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-surface-mid px-3 py-2"
          >
            <span className="text-sm text-text">{car.name}</span>
            <div className="flex items-center gap-3">
              {formatAlternativePrice(car.pricing) && (
                <span className="text-sm text-steel">{formatAlternativePrice(car.pricing)}</span>
              )}
              <a href={`/cars/${car.id}`} className="text-sm text-primary underline">
                View
              </a>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
